import { css, cx } from "@emotion/css";
import { EllipsisVerticalIcon, ExclamationTriangleIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, colorScales, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ReactNode } from "react";
import React, { useRef } from "react";
import { useContainsFocus, useIsHovered } from "../../hooks";
import { useOctopusLinkComponent } from "../../routing";
import { resetStyles } from "../../utils";
import { Badge } from "../Badge";
import { Button } from "../Button";
import { SimpleMenu, useMenuState } from "../Menu";
import { Tooltip } from "../Tooltip";
import { ProcessSidebarStepErrors } from "./ProcessSidebarStepErrors";
import type { ProcessStepData } from "./types";
export interface ProcessSidebarStepProps {
    stepData: ProcessStepData;
}
export function ProcessSidebarStep({ stepData }: ProcessSidebarStepProps) {
    const { id, name, href, icon, iconTooltip, isActive, isDisabled, errors, menuItems } = stepData;
    const Link = useOctopusLinkComponent();
    const linkId = `process-step-link-${id}`;
    const headerRef = useRef<HTMLDivElement>(null);
    const isHeaderHovered = useIsHovered(headerRef);
    const isHeaderContainsFocus = useContainsFocus(headerRef);
    const hasErrors = errors.length > 0;
    const isContainer = "containerSteps" in stepData;
    const hasNoErrorStyles = {
        [stateStyles.base]: true,
        [stateStyles.baseStep]: !isContainer,
        [stateStyles.baseStepContainer]: isContainer,
        [stateStyles.hover]: !isActive && isHeaderHovered,
        [stateStyles.activeStep]: isActive && !isContainer,
        [stateStyles.activeStepContainer]: isActive && isContainer,
    };
    const hasErrorStyles = {
        [errorStateStyles.base]: true,
        [errorStateStyles.hover]: !isActive && isHeaderHovered,
        [errorStateStyles.active]: isActive,
    };
    const [onOverflowMenuButtonClick, overflowMenuState, overflowMenuButtonAriaAttributes] = useMenuState();
    const containerBadgeLabel = isContainer ? (stepData.containerType === "rolling" ? "Rolling" : stepData.containerType === "template" ? "Template" : undefined) : undefined;
    const containerBadge = containerBadgeLabel ? <Badge size="small" variant="neutral" label={containerBadgeLabel}/> : undefined;
    const showOverflowMenuButton = menuItems && menuItems.length > 0 && (isHeaderHovered || isHeaderContainsFocus || overflowMenuState.isOpen);
    const renderWithErrorMessageTooltip = (content: ReactNode) => {
        return hasErrors ? (<Tooltip className={styles.errorTooltipContainer} content={<ProcessSidebarStepErrors errors={errors}/>}>
                {content}
            </Tooltip>) : (content);
    };
    return (<div className={cx(styles.root, hasErrors ? hasErrorStyles : hasNoErrorStyles, getRootFocusStyles(linkId))}>
            <div ref={headerRef} className={styles.header}>
                {renderWithErrorMessageTooltip(<Link id={linkId} className={cx(styles.link, isContainer ? styles.stepContainerLink : styles.stepLink)} href={href} title={name}>
                        <Tooltip className={cx(styles.iconContainer, { [styles.iconDisabled]: isDisabled })} content={iconTooltip}>
                            {"src" in icon ? <img className={styles.icon} src={icon.src} alt=""/> : icon}
                        </Tooltip>
                        <span className={cx(styles.name, { [styles.disabled]: isDisabled, [styles.nameActive]: isActive })}>{name}</span>
                        {!showOverflowMenuButton &&
                (hasErrors ? (<span className={styles.errorIcon}>
                                    <ExclamationTriangleIcon size={24}/>
                                </span>) : (containerBadge))}
                    </Link>)}
                {showOverflowMenuButton && (<div className={styles.overflowButton}>
                        <Button icon={<EllipsisVerticalIcon size={20}/>} importance="quiet" size="small" onClick={onOverflowMenuButtonClick} accessibleName={`${overflowMenuState.isOpen ? "Hide" : "Show"} ${name} step menu`} {...overflowMenuButtonAriaAttributes}/>
                        <SimpleMenu accessibleName={`${name} step menu`} items={menuItems} menuState={overflowMenuState}/>
                    </div>)}
            </div>
            {isContainer && (<div className={styles.children}>
                    {stepData.containerSteps.map((stepData) => (<ProcessSidebarStep key={stepData.id} stepData={stepData}/>))}
                </div>)}
        </div>);
}
const styles = {
    root: css({
        display: "flex",
        flexGrow: 1,
        minWidth: 0,
        flexDirection: "column",
        borderRadius: borderRadius.small,
        borderWidth: borderWidth[1],
        borderStyle: "solid",
        minHeight: "2.75rem",
        boxSizing: "border-box",
    }),
    header: css({
        display: "flex",
        flexGrow: 1,
    }),
    link: css({
        ...resetStyles.anchor,
        display: "flex",
        flex: 1,
        minWidth: 0,
        alignItems: "center",
        gap: space[8],
        "&:hover": {
            color: "inherit", // prevent global link hover styles applying
        },
    }),
    stepLink: css({
        padding: `${space[8]} ${space[12]}`,
    }),
    stepContainerLink: css({
        padding: `${space[12]} ${space[12]} ${space[8]} ${space[12]}`,
    }),
    children: css({
        display: "flex",
        flexDirection: "column",
        gap: space[8],
        padding: `0 ${space[12]} ${space[12]} ${space[12]}`,
    }),
    disabled: css({
        textDecoration: "line-through",
    }),
    iconContainer: css({
        background: themeTokens.color.background.primary.default,
        borderRadius: borderRadius.extraSmall,
        height: "1.5rem",
        width: "1.5rem",
        overflow: "clip",
    }),
    icon: css({
        display: "block",
        objectFit: "scale-down",
        height: "1.5rem",
        width: "1.5rem",
    }),
    iconDisabled: css({
        opacity: 0.5,
    }),
    name: css({
        flex: 1,
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "clip",
        minWidth: 0,
        font: text.body.regular.small,
    }),
    nameActive: css({
        font: text.body.bold.small,
    }),
    errorTooltipContainer: css({
        flex: 1,
        minWidth: 0,
    }),
    errorIcon: css({
        color: themeTokens.color.icon.danger,
    }),
    overflowButton: css({
        alignSelf: "center",
        marginRight: space[8],
    }),
};
function getRootFocusStyles(linkId: string) {
    return css({
        [`:has(#${linkId}:focus-visible)`]: {
            boxShadow: themeTokens.shadow.focused,
        },
    });
}
const stateStyles = {
    base: css({
        color: themeTokens.color.text.primary,
        backgroundColor: themeTokens.color.background.primary.default,
    }),
    baseStep: css({
        borderColor: themeTokens.color.border.primary,
    }),
    baseStepContainer: css({
        borderColor: colorScales.blue[300],
    }),
    hover: css({
        borderColor: colorScales.blue[300],
        backgroundColor: colorScales.blue[100],
    }),
    activeStep: css({
        color: themeTokens.color.text.inverse,
        backgroundColor: themeTokens.color.button.background.loud.default,
    }),
    activeStepContainer: css({
        borderColor: themeTokens.color.border.selected,
        backgroundColor: colorScales.blue[100],
    }),
};
const errorStateStyles = {
    base: css({
        borderColor: themeTokens.color.border.danger,
        backgroundColor: colorScales.red[100],
    }),
    hover: css({
        backgroundColor: colorScales.red[200],
    }),
    active: css({
        background: colorScales.red[200],
    }),
};
